import { PcbLayerType, SchLayerType } from '@cadstrom/sigma-engine-shared';
import { useMemo } from 'react';

import { useLocalColorScheme } from '#utils/use-local-color-scheme.js';
import { getColorSchemeForCadTool } from '../color-provider/cad-tool-color-schemes.js';
import { CadstromColorProvider } from '../color-provider/cadstrom-color-provider.js';
import { CadstromColorProviderContext } from './cadstrom-color-provider-context.js';
import { useCoreModelContext } from './core-model-context.js';

import type { Layer, LayerType, PcbModel, SchematicModel } from '@cadstrom/sigma-engine-shared';
import type { ReactNode } from 'react';
import type { IColorSchemeSet } from '../color-provider/cadstrom-color-provider.js';
import type { CadstromColorProviderContextValue } from './cadstrom-color-provider-context.js';

/**
 * Builds one synthetic layer per layer type so a colour provider can answer type-based lookups
 * without a concrete design layer set.
 *
 * @param types - The layer types to create layers for.
 * @returns One layer per type, with stable ids assigned in order.
 */
function createLayersForTypes(types: LayerType[]): Layer[] {
	return types.map((type, index) => ({ id: index + 1, name: type, type }));
}

export type CadstromColorProviderContextProviderProps = {
	/** Subtree that can consume the context. */
	children: ReactNode;
	/**
	 * Colour scheme the providers resolve colours from. Defaults to the scheme of the tool that
	 * authored the core model, so drawings look like they do in that tool.
	 */
	colorScheme?: IColorSchemeSet;
};

/**
 * Provides theme-aware {@link CadstromColorProvider}s for schematic and PCB drawings.
 *
 * When mounted under a `CoreModelContext`, one shared provider is built per layer set from the
 * model's real layers, and `schematic`/`pcb` resolve to the first provider of each drawing type —
 * every consumer reads and watches the same colour state. Without a core model, synthetic
 * type-based providers are supplied instead.
 *
 * @param props.children - Subtree that can consume the context.
 * @param props.colorScheme - Colour scheme the providers resolve colours from; defaults to the authoring
 *   tool's scheme.
 */
export function CadstromColorProviderContextProvider({
	children,
	colorScheme: colorSchemeOverride,
}: CadstromColorProviderContextProviderProps) {
	const [colorSchemeRef, appColorScheme] = useLocalColorScheme();
	const coreModel = useCoreModelContext(false)?.coreModel;

	const value = useMemo<CadstromColorProviderContextValue>(() => {
		const colorScheme = colorSchemeOverride ?? getColorSchemeForCadTool(coreModel?.type);
		const themeMode = appColorScheme === 'light' ? 'light' : 'dark'; // Default to dark scheme before the actual scheme is known
		const modelTypeByLayerSet = new Map<string, PcbModel['modelType'] | SchematicModel['modelType']>();
		for (const design of Object.values(coreModel?.designs ?? {})) {
			if (!modelTypeByLayerSet.has(design.layerSet)) {
				modelTypeByLayerSet.set(design.layerSet, design.modelType);
			}
		}

		const byLayerSet = new Map<string, CadstromColorProvider>();
		let schematic: CadstromColorProvider | undefined;
		let pcb: CadstromColorProvider | undefined;
		for (const [layerSetId, layers] of Object.entries(coreModel?.layerSets ?? {})) {
			const modelType = modelTypeByLayerSet.get(layerSetId);
			if (modelType === undefined) {
				continue;
			}

			const provider = new CadstromColorProvider(colorScheme, themeMode, modelType, layers, true);
			byLayerSet.set(layerSetId, provider);
			if (modelType === 'schematic') {
				schematic ??= provider;
			} else {
				pcb ??= provider;
			}
		}

		return {
			schematic:
				schematic ??
				new CadstromColorProvider(
					colorScheme,
					themeMode,
					'schematic',
					createLayersForTypes(Object.values(SchLayerType)),
					true,
				),
			pcb:
				pcb ??
				new CadstromColorProvider(
					colorScheme,
					themeMode,
					'pcb',
					createLayersForTypes(Object.values(PcbLayerType)),
					true,
				),
			byLayerSet,
		};
	}, [appColorScheme, colorSchemeOverride, coreModel]);

	return (
		<CadstromColorProviderContext value={value}>
			{children}

			{/* Render a hidden element so that we can read the local colour scheme */}
			<div ref={colorSchemeRef} aria-hidden className='fixed -top-full -left-full hidden' />
		</CadstromColorProviderContext>
	);
}
